import { Controller } from "@hotwired/stimulus";

const CHART_CDN = "https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js";

export default class extends Controller {
  static values = {
    labels: Array,
    values: Array,
    label: String,
    color: { type: String, default: "#3b82f6" },
    currency: { type: Boolean, default: false },
    format: { type: String, default: "number" },
    chartType: { type: String, default: "bar" },
  };

  connect() {
    this.loadChart().then(() => this.render());
  }

  disconnect() {
    this.chart?.destroy();
  }

  loadChart() {
    if (window.Chart) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = CHART_CDN;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  render() {
    const canvas = this.element.querySelector("canvas");
    if (!canvas || !window.Chart) return;

    const isLine = this.chartTypeValue === "line";
    const isPercent = this.formatValue === "percent";

    this.chart = new window.Chart(canvas, {
      type: this.chartTypeValue,
      data: {
        labels: this.labelsValue,
        datasets: [
          {
            label: this.labelValue,
            data: this.valuesValue,
            backgroundColor: isLine ? `${this.colorValue}33` : this.colorValue,
            borderColor: this.colorValue,
            borderWidth: isLine ? 2 : 0,
            borderRadius: isLine ? 0 : 4,
            fill: isLine,
            tension: isLine ? 0.3 : 0,
            pointRadius: isLine ? 3 : 0,
            pointHoverRadius: isLine ? 5 : 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 4, right: 8, bottom: 4, left: 4 },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) =>
                `${context.dataset.label}: ${this.formatValueLabel(context.parsed.y)}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              autoSkip: true,
              maxRotation: 45,
              minRotation: 0,
              maxTicksLimit: 12,
              font: { size: 11 },
              padding: 4,
            },
          },
          y: {
            beginAtZero: true,
            max: isPercent ? 100 : undefined,
            ticks: {
              precision: isPercent ? 1 : 0,
              padding: 4,
              callback: (value) => this.formatValueLabel(value),
            },
          },
        },
      },
    });
  }

  formatValueLabel(value) {
    const formatted = Number(value).toLocaleString("en-US");
    if (this.formatValue === "percent") return `${formatted}%`;
    if (this.currencyValue) return `RD$${formatted}`;
    return formatted;
  }
}
